"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.knowledgeOpenApiDocs = exports.KnowledgeSchema = void 0;
// Standard error response schema with error_code support
const ErrorResponse = {
    type: 'object',
    properties: {
        success: {
            type: 'boolean',
            example: false
        },
        error: {
            type: 'string',
            description: 'Human-readable error message'
        },
        error_code: {
            type: 'integer',
            description: 'Numeric error code for programmatic handling'
        }
    }
};
const FileSchema = {
    type: 'object',
    description: 'File attachment metadata',
    properties: {
        id: {
            type: 'number',
            description: 'Unique file identifier (for existing files)'
        },
        filename: {
            type: 'string',
            description: 'System filename'
        },
        originalName: {
            type: 'string',
            description: 'Original filename with extension'
        },
        mimeType: {
            type: 'string',
            description: 'MIME type of the file',
            example: 'application/pdf'
        },
        size: {
            type: 'number',
            description: 'File size in bytes'
        },
        path: {
            type: 'string',
            description: 'Base64 encoded file content (for uploads)'
        }
    }
};
exports.KnowledgeSchema = {
    type: 'object',
    description: 'Knowledge entry with content and metadata (IKnowledge)',
    properties: {
        id: {
            type: 'number',
            description: 'Unique knowledge entry identifier'
        },
        title: {
            type: 'string',
            description: 'Knowledge entry title',
            example: 'Introduction to Machine Learning'
        },
        content: {
            type: 'string',
            description: 'Knowledge content (text content or URL for google-drive/link type)'
        },
        contentType: {
            type: 'string',
            enum: ['text', 'file', 'link', 'google-drive'],
            description: 'Type of knowledge content'
        },
        description: {
            type: 'string',
            description: 'Brief description of the knowledge entry'
        },
        tags: {
            type: 'array',
            items: { type: 'string' },
            description: 'Tags for categorizing and searching knowledge'
        },
        native: {
            type: 'boolean',
            description: 'Whether this is a system/native knowledge entry'
        },
        active: {
            type: 'boolean',
            description: 'Whether the knowledge entry is active'
        },
        creator: {
            type: 'object',
            description: 'User who created this knowledge entry (IUser)',
            properties: {
                id: { type: 'number' },
                name: { type: 'string' },
                username: { type: 'string' },
                email: { type: 'string' }
            }
        },
        ragStatus: {
            type: 'string',
            enum: ['pending', 'processing', 'completed', 'failed', 'not_indexed'],
            description: 'RAG indexing status (KnowledgeRAGStatus enum)'
        },
        ragJobId: {
            type: 'string',
            description: 'Job ID for RAG indexing process'
        },
        ragIndexedAt: {
            type: 'string',
            format: 'date-time',
            description: 'Timestamp when RAG indexing was completed'
        },
        ragError: {
            type: 'string',
            description: 'Error message if RAG indexing failed'
        },
        ragOverrideEnabled: {
            type: 'boolean',
            description: 'Whether custom RAG chunking settings are enabled for this entry'
        },
        ragOverride: {
            type: 'object',
            description: 'Custom RAG chunking configuration (IRagOverride)',
            properties: {
                chunkSize: { type: 'integer', description: 'Token chunk size' },
                chunkOverlap: { type: 'integer', description: 'Token overlap between chunks' },
                separators: { type: 'array', items: { type: 'string' }, description: 'Text separators for chunking' }
            }
        },
        timed: {
            type: 'boolean',
            description: 'Whether this knowledge entry has a validity time window'
        },
        startTime: {
            type: 'string',
            format: 'date-time',
            description: 'Start of the validity window (when timed is true)'
        },
        endTime: {
            type: 'string',
            format: 'date-time',
            description: 'End of the validity window (when timed is true)'
        },
        project: {
            type: 'object',
            description: 'Associated project (IProject)'
        },
        file: {
            ...FileSchema,
            description: 'Associated file (for file content type, IFile)'
        },
        created_at: {
            type: 'string',
            format: 'date-time'
        },
        updated_at: {
            type: 'string',
            format: 'date-time'
        }
    }
};
exports.knowledgeOpenApiDocs = {
    'knowledge.list': {
        description: 'List knowledge entries with filtering and pagination',
        payload: {
            query: {
                type: 'object',
                properties: {
                    p: {
                        type: 'integer',
                        description: 'Page number (1-indexed)',
                        minimum: 1,
                        default: 1
                    },
                    per_page: {
                        type: 'integer',
                        description: 'Number of items per page',
                        minimum: 1,
                        maximum: 100,
                        default: 20
                    },
                    all: {
                        type: 'integer',
                        description: 'Return all results without pagination (1 for true, 0 for false)',
                        enum: [0, 1]
                    },
                    projectId: {
                        type: 'string',
                        description: 'Filter by project ID'
                    },
                    picker: {
                        type: 'integer',
                        description: 'Picker mode for selection UI (1 for enabled)',
                        enum: [0, 1]
                    },
                    assigned: {
                        type: 'string',
                        description: 'Filter by assignment status'
                    },
                    projectSearch: {
                        type: 'string',
                        description: 'Search within project associations'
                    },
                    avatarId: {
                        type: 'string',
                        description: 'Filter by avatar ID to show knowledge entries assigned to specific avatar'
                    },
                    byId: {
                        type: 'string',
                        description: 'Comma-separated list of knowledge IDs to fetch specific entries (full data mode is enabled in this request - all DB fields will be returned)'
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'List of knowledge entries',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: {
                            type: 'array',
                            items: exports.KnowledgeSchema
                        },
                        count: {
                            type: 'integer',
                            description: 'Total number of knowledge entries matching the query'
                        }
                    }
                }
            },
            400: {
                description: 'Bad request',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to fetch knowledge entries',
                returnParams: ErrorResponse
            }
        }
    },
    'knowledge.get': {
        description: 'Get a single knowledge entry by ID',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Knowledge entry ID',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Knowledge entry details',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.KnowledgeSchema
                    }
                }
            },
            404: {
                description: 'Knowledge not found or access denied',
                returnParams: {
                    success: { type: 'boolean', example: false },
                    data: { type: 'null' },
                    error: { type: 'string' }
                }
            },
            500: {
                description: 'Failed to fetch knowledge',
                returnParams: ErrorResponse
            }
        }
    },
    'knowledge.create': {
        description: 'Create a new knowledge entry',
        payload: {
            body: {
                type: 'object',
                description: 'Knowledge entry creation data',
                properties: {
                    title: {
                        type: 'string',
                        description: 'Knowledge entry title',
                        required: true,
                        example: 'Introduction to AI'
                    },
                    description: {
                        type: 'string',
                        description: 'Brief description of the knowledge entry'
                    },
                    content: {
                        type: 'string',
                        description: 'Knowledge content (text content or Google Drive URL)'
                    },
                    contentType: {
                        type: 'string',
                        enum: ['text', 'file', 'google-drive'],
                        description: 'Type of knowledge content',
                        default: 'text'
                    },
                    tags: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Tags for categorization'
                    },
                    file: {
                        ...FileSchema,
                        description: 'File to upload (for file content type)'
                    },
                    projectId: {
                        type: 'string',
                        description: 'Associate with a project (optional)'
                    }
                },
                required: ['title']
            }
        },
        responses: {
            200: {
                description: 'Knowledge entry created successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.KnowledgeSchema
                    }
                }
            },
            400: {
                description: 'Bad request - validation error',
                returnParams: ErrorResponse
            },
            402: {
                description: 'Payment required - knowledge limit exceeded',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to create knowledge',
                returnParams: ErrorResponse
            }
        }
    },
    'knowledge.update': {
        description: 'Update an existing knowledge entry',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Knowledge entry ID',
                        required: true
                    }
                }
            },
            body: {
                type: 'object',
                description: 'Knowledge entry update data',
                properties: {
                    title: {
                        type: 'string',
                        description: 'Knowledge entry title'
                    },
                    description: {
                        type: 'string',
                        description: 'Brief description'
                    },
                    content: {
                        type: 'string',
                        description: 'Knowledge content'
                    },
                    contentType: {
                        type: 'string',
                        enum: ['text', 'file', 'google-drive'],
                        description: 'Type of knowledge content'
                    },
                    tags: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Tags for categorization'
                    },
                    file: {
                        ...FileSchema,
                        description: 'File to upload or update'
                    },
                    projectId: {
                        type: 'string',
                        description: 'Associate with a project (use empty string to clear)'
                    },
                    active: {
                        type: 'boolean',
                        description: 'Active status (super admin or parent user only)'
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Knowledge entry updated successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.KnowledgeSchema
                    }
                }
            },
            400: {
                description: 'Bad request - validation error',
                returnParams: ErrorResponse
            },
            404: {
                description: 'Knowledge not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to update knowledge',
                returnParams: ErrorResponse
            }
        }
    },
    'knowledge.reindex': {
        description: 'Force re-indexing of a knowledge entry for RAG processing',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Knowledge entry ID',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Re-indexing queued successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.KnowledgeSchema,
                        message: {
                            type: 'string',
                            description: 'Success message with job ID',
                            example: 'Re-indexing queued successfully with job ID: abc-123'
                        }
                    }
                }
            },
            400: {
                description: 'Bad request - cannot re-index text-type entries',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: false },
                        data: exports.KnowledgeSchema,
                        error: {
                            type: 'string',
                            example: 'Text-type knowledge entries cannot be re-indexed'
                        }
                    }
                }
            },
            404: {
                description: 'Knowledge not found',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Failed to queue re-indexing',
                returnParams: ErrorResponse
            }
        }
    },
    'knowledge.delete': {
        description: 'Delete a knowledge entry (soft delete for normal users, hard delete for admin)',
        payload: {
            params: {
                type: 'object',
                properties: {
                    id: {
                        type: 'number',
                        description: 'Knowledge entry ID',
                        required: true
                    }
                }
            }
        },
        responses: {
            200: {
                description: 'Knowledge entry deleted successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        data: exports.KnowledgeSchema
                    }
                }
            },
            404: {
                description: 'Knowledge not found',
                returnParams: {
                    success: { type: 'boolean', example: false },
                    data: { type: 'null' },
                    error: { type: 'string' }
                }
            },
            500: {
                description: 'Failed to delete knowledge',
                returnParams: ErrorResponse
            }
        }
    },
    'knowledge.bulk-upload': {
        description: 'Bulk upload multiple files as knowledge entries. Supports archive extraction (ZIP, TAR, etc.)',
        payload: {
            body: {
                type: 'object',
                description: 'Bulk upload request with files array',
                properties: {
                    files: {
                        type: 'array',
                        description: 'Array of files to upload. Archive files will be automatically extracted.',
                        items: FileSchema,
                        minItems: 1
                    },
                    projectId: {
                        type: 'string',
                        description: 'Associate all uploaded knowledge entries with a project'
                    },
                    tags: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Tags to apply to all uploaded knowledge entries'
                    }
                },
                required: ['files']
            }
        },
        responses: {
            200: {
                description: 'Bulk upload completed (may include partial failures)',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean' },
                        results: {
                            type: 'array',
                            description: 'Upload results for each file',
                            items: {
                                type: 'object',
                                properties: {
                                    file: { type: 'string', description: 'Filename' },
                                    success: { type: 'boolean' },
                                    knowledgeId: { type: 'string', description: 'Created knowledge ID (if successful)' },
                                    error: { type: 'string', description: 'Error message (if failed)' }
                                }
                            }
                        },
                        successCount: { type: 'integer' },
                        errorCount: { type: 'integer' }
                    }
                }
            },
            400: {
                description: 'Bad request - no files provided',
                returnParams: {
                    success: { type: 'boolean', example: false },
                    results: { type: 'array' },
                    successCount: { type: 'integer', example: 0 },
                    errorCount: { type: 'integer', example: 1 }
                }
            },
            402: {
                description: 'Payment required - bulk upload limit exceeded',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Bulk upload failed',
                returnParams: {
                    success: { type: 'boolean', example: false },
                    results: { type: 'array' },
                    successCount: { type: 'integer', example: 0 },
                    errorCount: { type: 'integer', example: 1 }
                }
            }
        }
    },
    'knowledge.google-drive-directory-upload': {
        description: 'Bulk upload from Google Drive directory. Processes supported file types recursively.',
        payload: {
            body: {
                type: 'object',
                description: 'Google Drive directory upload request',
                properties: {
                    directoryId: {
                        type: 'string',
                        description: 'Google Drive directory/folder ID to process',
                        required: true,
                        example: '1abc2def3ghi4jkl5mno'
                    },
                    projectId: {
                        type: 'string',
                        description: 'Associate all uploaded knowledge entries with a project'
                    },
                    tags: {
                        type: 'array',
                        items: { type: 'string' },
                        description: 'Tags to apply to all uploaded knowledge entries'
                    }
                },
                required: ['directoryId']
            }
        },
        responses: {
            200: {
                description: 'Google Drive directory processing queued successfully',
                returnParams: {
                    type: 'object',
                    properties: {
                        success: { type: 'boolean', example: true },
                        results: {
                            type: 'array',
                            items: {
                                type: 'object',
                                properties: {
                                    file: { type: 'string' },
                                    success: { type: 'boolean' },
                                    jobId: { type: 'string', description: 'Background processing job ID' }
                                }
                            }
                        },
                        successCount: { type: 'integer' },
                        errorCount: { type: 'integer' },
                        message: { type: 'string', description: 'Status message with job ID' }
                    }
                }
            },
            400: {
                description: 'Bad request - directory ID required or no supported files found',
                returnParams: {
                    success: { type: 'boolean', example: false },
                    results: { type: 'array' },
                    successCount: { type: 'integer', example: 0 },
                    errorCount: { type: 'integer', example: 1 },
                    error: { type: 'string' }
                }
            },
            402: {
                description: 'Payment required - bulk upload limit exceeded',
                returnParams: ErrorResponse
            },
            500: {
                description: 'Google Drive directory processing failed',
                returnParams: {
                    success: { type: 'boolean', example: false },
                    results: { type: 'array' },
                    successCount: { type: 'integer', example: 0 },
                    errorCount: { type: 'integer', example: 1 },
                    error: { type: 'string' }
                }
            }
        }
    }
};
